import { z } from "zod";
import { DATE_PRESETS, REPORT_FORMATS, REPORT_TYPES } from "../enums";
import type {
  AdEntityStatus,
  AdPlatform,
  AttributionChannel,
  CancelReason,
  Channel,
  ConfirmationMethod,
  ConfirmationState,
  ConversationStatus,
  CustomerSource,
  DatePreset,
  OrderSource,
  OrderStatus,
  ReportFormat,
  ReportType,
} from "../enums";
import { emailSchema } from "./common";

// Analysis (WP8): Accueil dashboard, Analyse, Risque, Rapports and the global search. Money is integer minor units
// in the client currency; ad spend is converted from the ad account currency and the rates used are reported.

const isoDateTime = z.string().datetime({ offset: true });

export const analyticsPeriodQuery = z.object({
  preset: z.enum(DATE_PRESETS).default("7d"),
  from: isoDateTime.optional(),
  to: isoDateTime.optional(),
});
export type AnalyticsPeriodQuery = z.infer<typeof analyticsPeriodQuery>;

/** Calendar days in the client time zone ("YYYY-MM-DD"), and the previous period of the same length. */
export interface AnalyticsPeriodDto {
  preset: DatePreset;
  from: string;
  to: string;
  days: number;
  previousFrom: string;
  previousTo: string;
  timezone: string;
}

export interface AnalyticsKpiDto {
  value: number;
  previous: number;
  /** Percent change versus the previous period, null when the previous value is 0. */
  delta: number | null;
  /** One value per day of the current period. */
  spark: number[];
}

export interface AnalyticsCompareDto {
  value: number | null;
  previous: number | null;
  delta: number | null;
}

export type AnalyticsFxSource = "manual" | "daily" | "unavailable";

export interface AnalyticsFxRateDto {
  base: string;
  quote: string;
  rate: number | null;
  source: AnalyticsFxSource;
  /** Day of the stored daily rate (null for manual rates). */
  date: string | null;
}

/** Exchange rates used to convert ad spend (and orders in another currency) into the client currency. */
export interface AnalyticsFxDto {
  currency: string;
  rates: AnalyticsFxRateDto[];
  /** Currencies without any known rate: their amounts are left out of the totals. */
  missing: string[];
}

export const ANALYTICS_FX_SOURCE_LABELS: Record<AnalyticsFxSource, string> = {
  manual: "taux manuel du client",
  daily: "taux du jour",
  unavailable: "taux indisponible",
};

// ---------------------------------------------------------------------------------------------------------------
// Sidebar badges and Accueil dashboard

export interface NavBadgesDto {
  conversationsUnread: number;
  ordersToConfirm: number;
  adAlertsOpen: number;
  recommendationsOpen: number;
}

/** Onboarding steps of a new workspace ("Démarrez en quelques étapes" card of the Accueil), in display order. */
export const DASHBOARD_SETUP_STEPS = ["channels", "products", "carrier", "ads", "site", "ai"] as const;
export type DashboardSetupStep = (typeof DASHBOARD_SETUP_STEPS)[number];

export const DASHBOARD_SETUP_LABELS: Record<DashboardSetupStep, { title: string; description: string; link: string; action: string }> = {
  channels: {
    title: "Connecter WhatsApp / Messenger / Instagram",
    description: "Recevez tous les messages de vos clients dans une seule boîte de réception.",
    link: "/integrations/whatsapp",
    action: "Connecter",
  },
  products: {
    title: "Ajouter votre premier produit",
    description: "Le catalogue fixe les prix utilisés par les commandes, l'IA et vos pages de vente.",
    link: "/produits/nouveau",
    action: "Ajouter un produit",
  },
  carrier: {
    title: "Connecter votre transporteur",
    description: "Expédiez vos commandes et suivez chaque colis sans quitter DigiSubs.",
    link: "/integrations/livraison",
    action: "Connecter",
  },
  ads: {
    title: "Connecter Meta Ads (ou TikTok / Google)",
    description: "Suivez les dépenses, les ventes et le ROAS réel de vos campagnes.",
    link: "/integrations/meta",
    action: "Connecter",
  },
  site: {
    title: "Créer votre page de vente",
    description: "Publiez une landing page avec son formulaire de commande.",
    link: "/site",
    action: "Créer une page",
  },
  ai: {
    title: "Configurer l'IA et les réponses automatiques",
    description: "Décrivez votre activité dans le Business Brain pour des réponses fiables, jour et nuit.",
    link: "/ia/business-brain",
    action: "Configurer l'IA",
  },
};

export interface DashboardConversationDto {
  id: string;
  customerName: string;
  avatarUrl: string | null;
  channel: Channel;
  preview: string;
  lastMessageAt: string;
  lastMessageDirection: "inbound" | "outbound" | null;
  unreadCount: number;
  status: ConversationStatus;
}

export type DashboardConversationTab = "all" | "unread" | Channel;

export interface DashboardOrderDto {
  id: string;
  number: string;
  customerName: string;
  customerAvatarUrl: string | null;
  productsLabel: string;
  imageUrl: string | null;
  totalMinor: number;
  currency: string;
  status: OrderStatus;
  isLate: boolean;
  createdAt: string;
}

export interface DashboardTopProductDto {
  key: string;
  productId: string | null;
  name: string;
  imageUrl: string | null;
  units: number;
  revenueMinor: number;
}

export interface DashboardCampaignDto {
  key: string;
  id: string | null;
  externalId: string;
  platform: AdPlatform;
  name: string;
  spendMinor: number;
  /** Revenue of the orders attributed to the campaign in DigiSubs (net of refunds). */
  ordersRevenueMinor: number;
  /** Purchase value reported by the ad platform, converted to the client currency. */
  platformRevenueMinor: number;
  /** "orders" when DigiSubs attributed orders exist, else "platform" when the platform reports sales. */
  salesSource: "orders" | "platform" | null;
  salesMinor: number;
  roas: number | null;
  status: AdEntityStatus | null;
}

export interface DashboardCustomerDto {
  id: string;
  name: string;
  avatarUrl: string | null;
  source: CustomerSource;
  createdAt: string;
}

export interface DashboardDto {
  currency: string;
  period: AnalyticsPeriodDto;
  access: { orders: boolean; customers: boolean; conversations: boolean; shipping: boolean; ads: boolean };
  kpis: {
    /** Gross order totals of non cancelled orders minus refunds. */
    revenue: AnalyticsKpiDto;
    grossRevenueMinor: number;
    refundsMinor: number;
    orders: AnalyticsKpiDto;
    newCustomers: AnalyticsKpiDto;
    /** null when the member cannot see advertising data. */
    adSpend: AnalyticsKpiDto | null;
  };
  /** COD-true profitability: revenue of delivered orders, delivery rate and MER (revenue / ad spend). */
  cod: { deliveredRevenueMinor: number; delivered: number; returned: number; deliveryRate: number | null; mer: number | null };
  /** Daily series of the last 90 days (the chart slices 7 / 30 / 90 days). */
  trends: Array<{ date: string; revenueMinor: number; orders: number; adSpendMinor: number }>;
  salesByChannel: Array<{ channel: AttributionChannel; revenueMinor: number; orders: number }>;
  deliveries: { inTransit: number; delivered: number; preparing: number; late: number; returned: number };
  conversations: {
    counts: Record<DashboardConversationTab, number>;
    lists: Record<DashboardConversationTab, DashboardConversationDto[]>;
  } | null;
  recentOrders: DashboardOrderDto[];
  topProducts: DashboardTopProductDto[];
  campaigns: DashboardCampaignDto[] | null;
  newCustomers: DashboardCustomerDto[];
  /**
   * Onboarding status. `newWorkspace` stays true until the first order is recorded: the Accueil then keeps the
   * reference layout and no longer shows the onboarding card.
   */
  setup: { steps: Array<{ key: DashboardSetupStep; done: boolean }>; completed: number; total: number; newWorkspace: boolean };
  fx: AnalyticsFxDto;
}

// ---------------------------------------------------------------------------------------------------------------
// Analyse

export interface AnalysisProductDto {
  key: string;
  productId: string | null;
  name: string;
  imageUrl: string | null;
  units: number;
  orders: number;
  revenueMinor: number;
  deliveredUnits: number;
  returnedUnits: number;
  returnRate: number | null;
  /** Gross margin in percent of revenue, null when no catalog cost is known for the lines. */
  marginPct: number | null;
  marginMinor: number | null;
}

export interface AnalysisRateRowDto {
  orders: number;
  revenueMinor: number;
  delivered: number;
  returned: number;
  deliveryRate: number | null;
}

export interface AnalysisDto {
  currency: string;
  period: AnalyticsPeriodDto;
  summary: {
    orders: AnalyticsCompareDto;
    revenueMinor: AnalyticsCompareDto;
    averageOrderMinor: AnalyticsCompareDto;
    newCustomers: AnalyticsCompareDto;
    confirmationRate: AnalyticsCompareDto;
    deliveryRate: AnalyticsCompareDto;
    returnRate: AnalyticsCompareDto;
  };
  funnels: {
    /** Distinct visitor sessions per site event type in the period. */
    site: Array<{ step: "page_view" | "view_content" | "initiate_checkout" | "purchase" | "lead"; sessions: number }>;
    orders: Array<{ step: "created" | "confirmed" | "shipped" | "delivered"; count: number }>;
  };
  products: AnalysisProductDto[];
  channels: Array<AnalysisRateRowDto & { channel: AttributionChannel }>;
  sources: Array<AnalysisRateRowDto & { source: OrderSource }>;
  /** Monthly cohorts by first order month: size, repeat customers and retention (share ordering in month k). */
  cohorts: Array<{ month: string; size: number; repeaters: number; repeatRate: number | null; retention: Array<number | null> }>;
  governorates: Array<AnalysisRateRowDto & { governorate: string; returnRate: number | null }>;
  confirmation: {
    total: number;
    confirmed: number;
    refused: number;
    unreachable: number;
    pending: number;
    rate: number | null;
    medianMinutes: number | null;
    byMethod: Array<{ method: ConfirmationMethod; count: number }>;
  };
  fx: AnalyticsFxDto;
}

// ---------------------------------------------------------------------------------------------------------------
// Risque

export type AnalyticsRiskLevel = "low" | "medium" | "high";

export interface RiskCustomerDto {
  id: string;
  name: string;
  phone: string | null;
  governorate: string;
  score: number;
  level: AnalyticsRiskLevel;
  /** "computed" by the growth engine, or "history" when derived from returns and refusals. */
  source: "computed" | "history";
  factors: string[];
  orders: number;
  delivered: number;
  returned: number;
  refused: number;
  blocked: boolean;
}

export interface RiskRateRowDto {
  key: string;
  label: string;
  delivered: number;
  returned: number;
  rate: number | null;
}

export interface RiskRefusedOrderDto {
  id: string;
  number: string;
  customerName: string;
  phone: string;
  totalMinor: number;
  currency: string;
  confirmationState: ConfirmationState;
  cancelReason: CancelReason | null;
  createdAt: string;
}

export interface RiskDto {
  currency: string;
  period: AnalyticsPeriodDto;
  summary: { delivered: number; returned: number; returnRate: number | null; refusedOrders: number; highRiskCustomers: number; blockedCustomers: number };
  highRiskCustomers: RiskCustomerDto[];
  blockedCustomers: Array<{ id: string; name: string; phone: string | null; reason: string | null; orders: number; returned: number; updatedAt: string }>;
  returnRates: { byGovernorate: RiskRateRowDto[]; byCarrier: RiskRateRowDto[]; byProduct: RiskRateRowDto[] };
  refusedOrders: RiskRefusedOrderDto[];
}

// ---------------------------------------------------------------------------------------------------------------
// Rapports

export const REPORT_FREQUENCIES = ["none", "daily", "weekly", "monthly"] as const;
export type ReportFrequency = (typeof REPORT_FREQUENCIES)[number];

export const REPORT_FREQUENCY_LABELS: Record<ReportFrequency, string> = {
  none: "Aucun envoi",
  daily: "Chaque jour",
  weekly: "Chaque semaine",
  monthly: "Chaque mois",
};

/** Period covered by a scheduled send (manual runs use the definition's date range). */
export const REPORT_SCHEDULE_PERIOD_LABELS: Record<Exclude<ReportFrequency, "none">, string> = {
  daily: "la veille",
  weekly: "les 7 derniers jours complets",
  monthly: "le mois précédent",
};

export const REPORT_FORMAT_LABELS: Record<ReportFormat, string> = { csv: "CSV (tableur)", pdf: "PDF (synthèse)" };

export const REPORT_TYPE_DESCRIPTIONS: Record<ReportType, string> = {
  sales: "Chiffre d'affaires par jour, remboursements, canaux et produits les plus vendus.",
  orders: "Liste détaillée des commandes avec statut, client, montant et livraison.",
  deliveries: "Expéditions par transporteur, statuts, livrées et retournées.",
  ads: "Dépenses publicitaires par campagne, ventes attribuées et ROAS.",
  customers: "Nouveaux clients avec source, commandes et montant dépensé.",
  products: "Performance des produits : unités, chiffre d'affaires et taux de retour.",
  conversations: "Conversations par canal, statut et délai de première réponse.",
  cod: "Paiements à la livraison attendus, encaissés et versés par transporteur.",
};

/** Weekday of a weekly send: 1 = lundi ... 7 = dimanche (ISO). */
export const REPORT_WEEKDAY_LABELS: Record<number, string> = { 1: "Lundi", 2: "Mardi", 3: "Mercredi", 4: "Jeudi", 5: "Vendredi", 6: "Samedi", 7: "Dimanche" };

export const reportRangeSchema = z.object({
  preset: z.enum(DATE_PRESETS).default("30d"),
  from: isoDateTime.optional(),
  to: isoDateTime.optional(),
});
export type ReportRangeInput = z.infer<typeof reportRangeSchema>;

export const reportScheduleSchema = z
  .object({
    frequency: z.enum(REPORT_FREQUENCIES).default("none"),
    hour: z.number().int("Heure invalide").min(0, "Heure invalide").max(23, "Heure invalide").default(8),
    weekday: z.number().int().min(1, "Jour invalide").max(7, "Jour invalide").default(1),
    dayOfMonth: z.number().int().min(1, "Jour invalide").max(28, "Choisissez un jour entre 1 et 28").default(1),
    recipients: z.array(emailSchema).max(10, "10 destinataires maximum").default([]),
  })
  .superRefine((value, ctx) => {
    if (value.frequency !== "none" && value.recipients.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["recipients"], message: "Ajoutez au moins un destinataire pour l'envoi planifié" });
    }
  });
export type ReportScheduleInput = z.infer<typeof reportScheduleSchema>;

export const reportDefinitionInputSchema = z.object({
  name: z.string().trim().min(2, "Nom trop court").max(120, "120 caractères maximum"),
  type: z.enum(REPORT_TYPES),
  format: z.enum(REPORT_FORMATS).default("csv"),
  range: reportRangeSchema.default({ preset: "30d" }),
  schedule: reportScheduleSchema.default({ frequency: "none", hour: 8, weekday: 1, dayOfMonth: 1, recipients: [] }),
});
export type ReportDefinitionInput = z.infer<typeof reportDefinitionInputSchema>;

export const reportDefinitionUpdateSchema = z.object({
  name: z.string().trim().min(2, "Nom trop court").max(120, "120 caractères maximum").optional(),
  type: z.enum(REPORT_TYPES).optional(),
  format: z.enum(REPORT_FORMATS).optional(),
  range: reportRangeSchema.optional(),
  schedule: reportScheduleSchema.optional(),
});
export type ReportDefinitionUpdate = z.infer<typeof reportDefinitionUpdateSchema>;

/** One-off export without a saved definition. */
export const reportQuickRunSchema = z.object({
  type: z.enum(REPORT_TYPES),
  format: z.enum(REPORT_FORMATS).default("csv"),
  range: reportRangeSchema.default({ preset: "30d" }),
});
export type ReportQuickRunInput = z.infer<typeof reportQuickRunSchema>;

export const reportRunListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  reportId: z.string().regex(/^[a-f0-9]{24}$/i, "Identifiant invalide").optional(),
});
export type ReportRunListQuery = z.infer<typeof reportRunListQuery>;

export type ReportRunStatus = "running" | "completed" | "failed";

export interface ReportRunDto {
  id: string;
  reportId: string | null;
  name: string;
  type: ReportType;
  format: ReportFormat;
  status: ReportRunStatus;
  rows: number;
  fileName: string | null;
  size: number;
  periodFrom: string | null;
  periodTo: string | null;
  trigger: "manual" | "schedule";
  emailedTo: string[];
  error: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface ReportDefinitionDto {
  id: string;
  name: string;
  type: ReportType;
  format: ReportFormat;
  range: { preset: DatePreset; from: string | null; to: string | null };
  schedule: { frequency: ReportFrequency; hour: number; weekday: number; dayOfMonth: number; recipients: string[]; nextRunAt: string | null };
  lastRunAt: string | null;
  lastRunStatus: ReportRunStatus | null;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------------------------------------------
// Global search (Ctrl K)

export const GLOBAL_SEARCH_TYPES = ["customer", "order", "product", "conversation", "campaign", "lead"] as const;
export type GlobalSearchType = (typeof GLOBAL_SEARCH_TYPES)[number];

export const globalSearchQuery = z.object({
  q: z.string().trim().min(2, "Tapez au moins deux caractères").max(100, "Recherche trop longue"),
});
export type GlobalSearchQuery = z.infer<typeof globalSearchQuery>;

export interface GlobalSearchHitDto {
  type: GlobalSearchType;
  id: string;
  title: string;
  subtitle: string;
  link: string;
}

export interface GlobalSearchResultDto {
  items: GlobalSearchHitDto[];
}
